import { DateRange } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  NO_CLIENT,
  NO_EXTERNAL_ORDER_NUMBER,
  NO_MARKETPLACE_ORDER_NUMBER,
  NO_OPTION_MAP,
  NO_OPTIONS,
  NO_RESPONSIBILITY_DEPARTMENT,
  NO_RESPONSIBLE,
  NO_TAGS,
  NO_TYPE,
  NO_VENDOR,
  SELECT_ALL,
} from 'pages/production/controllers/production-filters-controller/constants';
import { ValueIdNameWithColorT } from 'pages/production/controllers/production-filters-controller/types';
import { ProductionFilterCriteriaEnum } from 'services/production-workflow.model';
import { IdName } from 'types/common-types';

dayjs.extend(utc);

export const returnParams = (values: IdName[], isGetName?: boolean) => {
  let valuesToReturn: string[] = [];
  if (isGetName) valuesToReturn = values.map((value) => value.name);

  if (!isGetName) valuesToReturn = values.map((value) => value.id);

  return valuesToReturn.length ? valuesToReturn : undefined;
};

export const convertToStockFilterParams = (values: IdName[]): boolean | undefined => {
  const hasYes = values.some((value) => value.name === 'Yes');
  const hasNo = values.some((value) => value.name === 'No');

  switch (true) {
    case hasYes && hasNo:
      return undefined;

    case hasYes:
      return true;

    case hasNo:
      return false;

    default:
      return undefined;
  }
};

export const convertToStockFilterOptions = (value: boolean | undefined, options: IdName[]): IdName[] => {
  if (value === true) {
    const yesOption = options.find((option) => option.name === 'Yes');
    return yesOption ? [yesOption] : [];
  }
  if (value === false) {
    const noOption = options.find((option) => option.name === 'No');
    return noOption ? [noOption] : [];
  }
  return [];
};

const mapArrayToObjectById = (array: IdName[]) => {
  return array.reduce((result, item) => {
    const res = { ...result };
    res[item.id] = { ...item };
    return res;
  }, {});
};

export const getValueFromIds = (values: string[] | undefined, options: IdName[]): IdName[] => {
  if (values) {
    const optionsMap = mapArrayToObjectById(options);
    return values.map((item) => optionsMap[item]);
  }
  return [];
};

export const getValues = (values: IdName[] | undefined): IdName[] => {
  if (values) {
    return values.map((i) => ({
      id: i.id === null ? 'null' : i.id,
      name: i.name,
    }));
  }
  return [];
};

export const setTagsIdNameWithColor = (
  values: IdName[] | undefined,
): { all: boolean; none: boolean; items?: ValueIdNameWithColorT[] } | undefined => {
  if (!values || values.length === 0) {
    return undefined;
  }

  const isNoneSelected = Boolean(values.find((item) => item?.id === NO_TAGS));
  const isAllSelected = Boolean(values.find((item) => item?.id === SELECT_ALL));

  if (isNoneSelected) {
    return {
      none: true,
      all: false,
      items: [],
    };
  }

  if (isAllSelected) {
    return {
      all: true,
      none: false,
      items: [],
    };
  }

  const items = values.map((i) => ({
    id: i.id === null ? 'null' : i.id,
    name: i.name,
    color: 'color' in i && typeof (i as ValueIdNameWithColorT).color === 'string' ? (i as ValueIdNameWithColorT).color : '',
    created_at: 'created_at' in i ? (i as ValueIdNameWithColorT).created_at : '',
  }));

  return {
    all: false,
    none: false,
    items,
  };
};

export const extractTagsIdNameWithColor = (
  values: { all: boolean; none: boolean; items?: ValueIdNameWithColorT[] } | undefined,
): ValueIdNameWithColorT[] | undefined => {
  if (values?.none) {
    return [
      {
        id: NO_TAGS,
        name: NO_TAGS,
        color: '#ffffff',
        created_at: '',
      },
    ];
  }

  if (values?.all) {
    return [
      {
        id: SELECT_ALL,
        name: SELECT_ALL,
        color: '#ffffff',
        created_at: '',
      },
    ];
  }

  return values?.items?.map((i) => ({
    id: i.id === null ? 'null' : i.id,
    name: i.name,
    color: 'color' in i && typeof (i as ValueIdNameWithColorT).color === 'string' ? (i as ValueIdNameWithColorT).color : '',
    created_at: 'created_at' in i ? (i as ValueIdNameWithColorT).created_at : '',
  }));
};

export const getDatesForFilters = (value: DateRange<Dayjs>): [string | null, string | null] | undefined => {
  const from = value[0];
  const to = value[1];

  const start = from && dayjs(from).isValid() ? dayjs(from).utc().startOf('day').toISOString() : null;
  const end = to && dayjs(to).isValid() ? dayjs(to).utc().endOf('day').toISOString() : null;

  return start !== null || end !== null ? [start, end] : undefined;
};

export const stringArrayToDateRange = (dateStrings: string[]): DateRange<Dayjs> => {
  if (!!dateStrings[0] && !dateStrings[1]) {
    const singleDate = dayjs(dateStrings[0]);
    return [singleDate, null];
  }
  if (!dateStrings[0] && !!dateStrings[1]) {
    const singleDate = dayjs(dateStrings[1]);
    return [null, singleDate];
  }
  const startDate = dayjs(dateStrings[0]);
  const endDate = dayjs(dateStrings[1]);
  return [startDate, endDate];
};

export const getIdName = (values: { id: string; name: string; [key: string]: any }[]) => {
  const valuesToReturn = values.map((i) => ({
    id: (i.id === 'null' ? null : i.id) as string,
    name: i.name,
  }));
  return valuesToReturn.length ? valuesToReturn : undefined;
};

export const getIdFullName = (items: { first_name: string; last_name: string; id: string; [key: string]: any }[]) => {
  return items.map((item) => ({ ...item, name: `${item.first_name} ${item.last_name}` }));
};

export const getDepartmentPath = (items: { path: IdName[]; id: string; name: string; [key: string]: any }[]) => {
  return items.map((item) => ({ ...item, path: item.path.map((i) => i.name).join('/') }));
};

export const extractOptionsFromSelectAllFilter = (
  value: IdName[],
):
  | {
      all: boolean;
      none: boolean;
      items: IdName[] | undefined;
    }
  | undefined => {
  const NONE_OPTION_IDS = [
    NO_TYPE,
    NO_VENDOR,
    NO_CLIENT,
    NO_OPTIONS,
    NO_RESPONSIBLE,
    NO_EXTERNAL_ORDER_NUMBER,
    NO_MARKETPLACE_ORDER_NUMBER,
    NO_RESPONSIBILITY_DEPARTMENT,
  ];
  const isAllSelected = Boolean(value.find((item) => item.id === SELECT_ALL));

  const isNoneSelected = Boolean(value.find((item) => NONE_OPTION_IDS.includes(item.id)));

  const shouldOmitItems = isAllSelected || isNoneSelected;
  const items = !shouldOmitItems && value.length > 0 ? value : undefined;

  const result = {
    all: isAllSelected,
    none: isNoneSelected,
    items,
  };

  if (!result.all && !result.none && !result.items) {
    return undefined;
  }

  return result;
};

export const transformSelectAllFilterToValues = (
  values: { none?: boolean; all?: boolean; items?: IdName[] } | null | undefined,
  key: ProductionFilterCriteriaEnum,
): IdName[] => {
  if (!values) return [];

  if (values.all) {
    return [
      {
        id: SELECT_ALL,
        name: SELECT_ALL,
      },
    ];
  }

  if (values.none) {
    const noOption = NO_OPTION_MAP[key];
    return noOption ? [noOption] : [];
  }

  if (values.items) {
    return values.items;
  }

  return [];
};
